import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  Activity,
  Users,
  Layers,
  Monitor,
  Clock,
  DollarSign,
  TrendingDown,
  AlertTriangle,
  Filter,
  Download,
  Search,
  X,
  BarChart3,
  Eye,
  Package,
  Calendar,
  FileText,
  Target,
  Shield,
  ExternalLink,
  Trophy,
  Globe,
  MapPin,
  ArrowLeft,
} from 'lucide-react';
import type { FacilityConfig } from '../../services/missionControl.js';
import { insightsService } from '../../services/insights.js';
import type { InsightsFacilityData } from '../../services/insights.js';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { C } from './data/constants.js';
import type { Procedure, Surgeon, TrendPoint, WasteItem } from './data/constants.js';

import { PillToggle } from './components/PillToggle.js';
import { MetricCard } from './components/MetricCard.js';
import { SectionHeader } from './components/SectionHeader.js';
import { ActionBtn } from './components/ActionBtn.js';
import { BenchmarkBar } from './components/BenchmarkBar.js';
import { LeaderboardRow } from './components/LeaderboardRow.js';
import { ComplianceHeatmap } from './components/ComplianceHeatmap.js';
import { DeadTimeBreakdown } from './components/DeadTimeBreakdown.js';
import { PredictedSavings } from './components/PredictedSavings.js';
import { WasteRow } from './components/WasteRow.js';
import { ProcedureRow } from './components/ProcedureRow.js';
import { ProcedureDetail } from './components/ProcedureDetail.js';
import { ROICard } from './components/ROICard.js';
import { EmptyState } from './components/EmptyState.js';
import './insights.css';

export function InsightsPage(): React.ReactElement {
  const { facilityId } = useParams<{ facilityId: string }>();
  const location = useLocation();
  const [facility, setFacility] = useState<FacilityConfig | null>(
    (location.state as { facility?: FacilityConfig } | null)?.facility ?? null
  );
  const [data, setData] = useState<InsightsFacilityData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!facilityId) return;

    const loadData = async () => {
      setLoading(true);
      const [facilities, insights] = await Promise.all([
        insightsService.getCommercialFacilities(),
        insightsService.getFacilityInsights(facilityId),
      ]);
      const match = facilities.find((f) => f.id === facilityId);
      if (match) setFacility(match);
      setData(insights);
      setLoading(false);
    };

    loadData();
  }, [facilityId]);

  const [timeRange, setTimeRange] = useState('all');
  const [trendRange, setTrendRange] = useState('30');
  const [valueTab, setValueTab] = useState('waste');
  const [wasteView, setWasteView] = useState('facility');

  const [coreView, setCoreView] = useState('surgeon');
  const [selectedProc, setSelectedProc] = useState<Procedure | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [minDuration, setMinDuration] = useState(10);
  const [leaderboardMetric, setLeaderboardMetric] = useState('volume');
  const [procedurePage, setProcedurePage] = useState(0);

  useEffect(() => {
    setProcedurePage(0);
  }, [searchQuery, minDuration]);

  // Real data from Firestore
  const surgeons: Surgeon[] = data?.surgeons ?? [];
  const specialties: string[] = data?.specialties ?? [];
  const orRooms: string[] = data?.orRooms ?? [];

  // Map API procedures to the frontend Procedure type
  const procedures: Procedure[] = useMemo(() => {
    if (!data?.procedures) return [];
    return data.procedures.map((p) => ({
      id: p.id,
      name: p.name,
      surgeon: p.surgeonName,
      surgeonId: p.surgeonId,
      or: p.roomName,
      date: p.startTime
        ? new Date(p.startTime).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '—',
      duration: Math.round(p.totalTime / 60),
      steps: p.steps,
      stepsCompleted: p.stepsCompleted,
      wasteItems: p.wasteItems,
    }));
  }, [data?.procedures]);

  // Count total waste items across all procedures
  const totalWasteItemCount = useMemo(() => {
    if (!data?.procedures) return 0;
    return data.procedures.reduce((sum, p) => sum + p.wasteItems.length, 0);
  }, [data?.procedures]);

  // Aggregate waste items across all procedures (if any exist)
  const allWasteItems: WasteItem[] | null = useMemo(() => {
    if (!data?.procedures) return null;
    const wasteMap = new Map<string, { count: number; cost: number }>();
    for (const p of data.procedures) {
      for (const w of p.wasteItems) {
        const existing = wasteMap.get(w.name);
        if (existing) {
          existing.count += w.count;
          existing.cost += w.cost;
        } else {
          wasteMap.set(w.name, { count: w.count, cost: w.cost });
        }
      }
    }
    if (wasteMap.size === 0) return null;
    return [...wasteMap.entries()]
      .map(([name, { count, cost }]) => ({ name, count, cost }))
      .sort((a, b) => b.count - a.count);
  }, [data?.procedures]);

  // Heatmap data from API
  const heatmapData = data?.heatmap?.data ?? null;
  const heatmapWeeks = data?.heatmap?.weeks ?? 0;

  // Data not yet available from Firestore — will be null until wired up
  const trendData = null as Record<string, TrendPoint[]> | null;
  const benchmarkData = null as {
    facility: {
      wastePerCase: number;
      deadTimePerCase: number;
      utilization: number;
      adoptionRate: number;
    };
    network: {
      wastePerCase: number;
      deadTimePerCase: number;
      utilization: number;
      adoptionRate: number;
    };
    topQuartile: {
      wastePerCase: number;
      deadTimePerCase: number;
      utilization: number;
      adoptionRate: number;
    };
  } | null;

  const trendDataCurrent = trendData?.[trendRange] ?? null;

  // Check if surgeons have the rich metric fields
  const hasWasteMetrics = surgeons.some((s) => s.wastePerCase != null);
  const hasDeadTimeMetrics = surgeons.some((s) => s.deadTimePerCase != null);
  const hasAdoptionMetrics = surgeons.some((s) => s.adoptionRate != null);

  const filteredProcedures = procedures.filter((p) => {
    if (p.duration < minDuration) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        p.surgeon.toLowerCase().includes(q) ||
        (p.specialty?.toLowerCase().includes(q) ?? false) ||
        p.or.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const PROCEDURES_PER_PAGE = 10;
  const totalProcedurePages = Math.ceil(filteredProcedures.length / PROCEDURES_PER_PAGE);
  const pagedProcedures = filteredProcedures.slice(
    procedurePage * PROCEDURES_PER_PAGE,
    (procedurePage + 1) * PROCEDURES_PER_PAGE
  );

  const sortedSurgeons = [...surgeons].sort((a, b) => {
    if (leaderboardMetric === 'waste' && a.wastePerCase != null && b.wastePerCase != null)
      return a.wastePerCase - b.wastePerCase;
    if (leaderboardMetric === 'deadtime' && a.deadTimePerCase != null && b.deadTimePerCase != null)
      return b.deadTimePerCase - a.deadTimePerCase;
    if (leaderboardMetric === 'adoption' && a.adoptionRate != null && b.adoptionRate != null)
      return b.adoptionRate - a.adoptionRate;
    return b.procedures - a.procedures;
  });

  // Build leaderboard pill options — always show Volume, conditionally show others
  const leaderboardOptions = [
    ...(hasWasteMetrics ? [{ label: 'Lowest Waste', value: 'waste' }] : []),
    ...(hasDeadTimeMetrics ? [{ label: 'Most Dead Time Saved', value: 'deadtime' }] : []),
    ...(hasAdoptionMetrics ? [{ label: 'Adoption', value: 'adoption' }] : []),
    { label: 'Volume', value: 'volume' },
  ];

  return (
    <div
      className="insights-page"
      style={{
        minHeight: '100vh',
        background: C.bg,
        color: '#fff',
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        {/* HEADER */}
        <header
          style={{
            padding: '32px 0 28px',
            borderBottom: `1px solid ${C.cardBorder}`,
            animation: 'insights-fadeSlideUp 0.5s ease',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <Link
                  to="/insights"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: C.textMid,
                    textDecoration: 'none',
                    marginRight: 4,
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = C.textMid)}
                >
                  <ArrowLeft size={14} />
                </Link>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 2.5,
                    color: C.accent,
                    textTransform: 'uppercase',
                    fontFamily: "'Space Mono', monospace",
                  }}
                >
                  eXeX Insights
                </div>
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: C.accent,
                    animation: 'insights-pulse 2s infinite',
                  }}
                />
              </div>
              <h1
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  margin: 0,
                  background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.7) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {data?.name ?? facility?.facilityName}
              </h1>
              <p
                style={{
                  fontSize: 13,
                  color: C.textDim,
                  margin: '6px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <MapPin size={12} /> {data?.location ?? facility?.location ?? facility?.region}
                {(data?.liveDate ?? facility?.liveDate) && (
                  <>
                    {' '}
                    &middot; Live since{' '}
                    {new Date((data?.liveDate ?? facility?.liveDate)!).toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </>
                )}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <ActionBtn icon={FileText} label="Board Report (PDF)" accent />
              <ActionBtn icon={Download} label="Export All Data" />
              <PillToggle
                options={[
                  { label: 'All Time', value: 'all' },
                  { label: 'Last 30 Days', value: '30' },
                ]}
                value={timeRange}
                onChange={setTimeRange}
              />
            </div>
          </div>
        </header>

        {/* SUMMARY METRICS */}
        <section style={{ padding: '28px 0' }}>
          <SectionHeader
            icon={Activity}
            title="Summary"
            subtitle="System usage and adoption overview"
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: 14,
            }}
          >
            <MetricCard
              icon={Users}
              label="Surgeons Onboarded"
              value={data?.totalSurgeons ?? 0}
              loading={loading}
              delay={0}
            />
            <MetricCard
              icon={Layers}
              label="Surgical Profiles"
              value={data?.totalProfiles ?? 0}
              loading={loading}
              delay={60}
            />
            <MetricCard
              icon={Monitor}
              label="Operating Rooms"
              value={data?.totalORs ?? 0}
              loading={loading}
              delay={120}
            />
            <MetricCard
              icon={BarChart3}
              label="Recorded Procedures"
              value={data?.recordedProcedures ?? 0}
              loading={loading}
              delay={180}
            />
            <MetricCard
              icon={Shield}
              label="Supported Procedures"
              value={data?.supportedProcedures ?? 0}
              loading={loading}
              delay={240}
            />
            <MetricCard icon={Target} label="Utilization Rate" value="Coming Soon" delay={300} />
          </div>
        </section>

        {/* GROWTH TREND */}
        <section style={{ padding: '12px 0 28px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: C.textMid }}>Growth Trend</span>
            <PillToggle
              options={[
                { label: '30 Days', value: '30' },
                { label: '60 Days', value: '60' },
                { label: '90 Days', value: '90' },
              ]}
              value={trendRange}
              onChange={setTrendRange}
            />
          </div>
          {trendDataCurrent ? (
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.cardBorder}`,
                borderRadius: 16,
                padding: '20px 16px 8px',
                animation: 'insights-fadeSlideUp 0.6s ease 100ms both',
              }}
            >
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trendDataCurrent}>
                  <defs>
                    <linearGradient id="gradProc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.accent} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={C.accent} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradSupp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.purple} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={C.purple} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 10, fill: C.textFaint }}
                    axisLine={false}
                    tickLine={false}
                    interval={Math.floor(trendDataCurrent.length / 8)}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: C.textFaint }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#14161A',
                      border: `1px solid ${C.cardBorder}`,
                      borderRadius: 10,
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="procedures"
                    stroke={C.accent}
                    strokeWidth={2}
                    fill="url(#gradProc)"
                    name="Recorded"
                  />
                  <Area
                    type="monotone"
                    dataKey="supported"
                    stroke={C.purple}
                    strokeWidth={2}
                    fill="url(#gradSupp)"
                    name="Supported"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState
              icon={BarChart3}
              title="Trend Data Not Available"
              message="Daily trend data is not yet being collected. Growth trends will appear once time-series data is available."
            />
          )}
        </section>

        {/* ROI + PROJECTED */}
        <section style={{ padding: '0 0 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ROICard />
            <PredictedSavings />
          </div>
        </section>

        {/* BENCHMARK COMPARISONS */}
        <section style={{ padding: '0 0 28px' }}>
          <SectionHeader
            icon={Globe}
            title="Network Benchmarks"
            subtitle="How this facility compares to the eXeX network (anonymized)"
            color={C.blue}
          />
          {benchmarkData ? (
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.cardBorder}`,
                borderRadius: 16,
                padding: '24px 28px',
                animation: 'insights-fadeSlideUp 0.5s ease',
              }}
            >
              <BenchmarkBar
                label="Avg Waste per Case"
                facility={benchmarkData.facility.wastePerCase}
                network={benchmarkData.network.wastePerCase}
                topQ={benchmarkData.topQuartile.wastePerCase}
                unit="$"
                inverse
              />
              <BenchmarkBar
                label="Avg Dead Time Reduced per Case"
                facility={benchmarkData.facility.deadTimePerCase}
                network={benchmarkData.network.deadTimePerCase}
                topQ={benchmarkData.topQuartile.deadTimePerCase}
                unit=" min"
              />
              <BenchmarkBar
                label="eXeX Utilization Rate"
                facility={benchmarkData.facility.utilization}
                network={benchmarkData.network.utilization}
                topQ={benchmarkData.topQuartile.utilization}
                unit="%"
              />
              <BenchmarkBar
                label="Surgeon Adoption Rate"
                facility={benchmarkData.facility.adoptionRate}
                network={benchmarkData.network.adoptionRate}
                topQ={benchmarkData.topQuartile.adoptionRate}
                unit="%"
              />
            </div>
          ) : (
            <EmptyState
              icon={Globe}
              title="Benchmark Data Not Available"
              message="Network benchmark comparisons will appear once waste and dead time metrics are being collected across the network."
            />
          )}
        </section>

        {/* LEADERBOARD */}
        <section style={{ padding: '0 0 28px' }}>
          <SectionHeader
            icon={Trophy}
            title="Surgeon Leaderboard"
            subtitle="Top performers driving efficiency"
            color={C.gold}
            actions={
              <PillToggle
                options={leaderboardOptions}
                value={leaderboardMetric}
                onChange={setLeaderboardMetric}
              />
            }
          />
          {loading ? (
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.cardBorder}`,
                borderRadius: 16,
                overflow: 'hidden',
              }}
            >
              {Array.from({ length: 4 }, (_, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 18px',
                    borderBottom: `1px solid ${C.cardBorder}`,
                  }}
                >
                  <span
                    style={{
                      width: 32,
                      textAlign: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                      color: C.textFaint,
                    }}
                  >
                    #{i + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      className="loading-blur"
                      style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}
                    >
                      Surgeon Name
                    </div>
                    <div
                      className="loading-blur"
                      style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}
                    >
                      Specialty
                    </div>
                  </div>
                  <div
                    className="loading-blur"
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: C.orange,
                      fontFamily: "'Space Mono', monospace",
                    }}
                  >
                    000
                  </div>
                </div>
              ))}
            </div>
          ) : surgeons.length === 0 ? (
            <EmptyState
              icon={Trophy}
              title="No Surgeon Data"
              message="No surgeons have been onboarded at this facility yet."
            />
          ) : (
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.cardBorder}`,
                borderRadius: 16,
                overflow: 'hidden',
              }}
            >
              {sortedSurgeons.map((s, i) => {
                let metric: string;
                let metricLabel: string;
                let metricColor: string;
                if (leaderboardMetric === 'waste' && s.wastePerCase != null) {
                  metric = `$${s.wastePerCase.toFixed(2)}`;
                  metricLabel = 'per case';
                  metricColor = C.accent;
                } else if (leaderboardMetric === 'deadtime' && s.deadTimePerCase != null) {
                  metric = `${s.deadTimePerCase}m`;
                  metricLabel = 'saved/case';
                  metricColor = C.purple;
                } else if (leaderboardMetric === 'adoption' && s.adoptionRate != null) {
                  metric = `${s.adoptionRate}%`;
                  metricLabel = 'adoption';
                  metricColor = C.blue;
                } else {
                  metric = `${s.procedures}`;
                  metricLabel = 'cases';
                  metricColor = C.orange;
                }
                return (
                  <LeaderboardRow
                    key={s.id}
                    surgeon={s}
                    rank={i + 1}
                    metric={metric}
                    metricLabel={metricLabel}
                    metricColor={metricColor}
                    delay={i * 60}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* COMPLIANCE HEATMAP + DEAD TIME BREAKDOWN */}
        <section style={{ padding: '0 0 28px' }}>
          <SectionHeader
            icon={Calendar}
            title="Compliance & Workflow Analysis"
            subtitle="Usage patterns and dead time breakdown by workflow phase"
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ComplianceHeatmap data={heatmapData ?? undefined} weeks={heatmapWeeks || undefined} />
            <DeadTimeBreakdown />
          </div>
        </section>

        {/* VALUE SECTION */}
        <section style={{ padding: '0 0 28px' }}>
          <SectionHeader
            icon={DollarSign}
            title="Value"
            subtitle="Waste identification and dead time reduction"
            actions={
              <PillToggle
                options={[
                  { label: 'Waste', value: 'waste' },
                  { label: 'Dead Time', value: 'deadtime' },
                ]}
                value={valueTab}
                onChange={setValueTab}
              />
            }
          />

          {valueTab === 'waste' ? (
            allWasteItems ? (
              <div style={{ animation: 'insights-fadeSlideUp 0.4s ease' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 14,
                    marginBottom: 20,
                  }}
                >
                  <MetricCard
                    icon={Package}
                    label="Total Waste Items"
                    value={allWasteItems.reduce((sum, w) => sum + w.count, 0)}
                    delay={0}
                  />
                  <MetricCard
                    icon={DollarSign}
                    label="Waste Value Identified"
                    value={allWasteItems.reduce((sum, w) => sum + w.cost, 0)}
                    prefix="$"
                    delay={60}
                  />
                  <MetricCard
                    icon={TrendingDown}
                    label="Avg Waste per Case"
                    value={
                      procedures.length > 0
                        ? +(
                            allWasteItems.reduce((s, w) => s + w.cost, 0) / procedures.length
                          ).toFixed(2)
                        : 0
                    }
                    prefix="$"
                    decimals={2}
                    delay={120}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 14,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <PillToggle
                    options={[
                      { label: 'Facility', value: 'facility' },
                      { label: 'Surgeon', value: 'surgeon' },
                      { label: 'Procedure', value: 'procedure' },
                      { label: 'Specialty', value: 'specialty' },
                    ]}
                    value={wasteView}
                    onChange={setWasteView}
                  />
                  <ActionBtn icon={ExternalLink} label="Update Consumable Costs" accent />
                </div>
                {trendDataCurrent && (
                  <div
                    style={{
                      background: C.card,
                      border: `1px solid ${C.cardBorder}`,
                      borderRadius: 16,
                      padding: '16px',
                      marginBottom: 20,
                    }}
                  >
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={trendDataCurrent.slice(-30)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: 10, fill: C.textFaint }}
                          axisLine={false}
                          tickLine={false}
                          interval={4}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: C.textFaint }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: '#14161A',
                            border: `1px solid ${C.cardBorder}`,
                            borderRadius: 10,
                            fontSize: 12,
                          }}
                        />
                        <Bar
                          dataKey="waste"
                          fill={C.orange}
                          radius={[3, 3, 0, 0]}
                          name="Waste ($)"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div
                  style={{
                    background: C.card,
                    border: `1px solid ${C.cardBorder}`,
                    borderRadius: 16,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '14px 16px',
                      borderBottom: `1px solid ${C.cardBorder}`,
                      display: 'grid',
                      gridTemplateColumns: '1fr 80px 90px 70px',
                      fontSize: 11,
                      color: C.textFaint,
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    <span>Item</span>
                    <span style={{ textAlign: 'right' }}>Count</span>
                    <span style={{ textAlign: 'right' }}>Cost</span>
                    <span style={{ textAlign: 'right' }}>Trend</span>
                  </div>
                  {allWasteItems.map((item, i) => (
                    <WasteRow
                      key={item.name}
                      item={item}
                      index={i}
                      maxCount={allWasteItems[0]?.count ?? 1}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ animation: 'insights-fadeSlideUp 0.4s ease' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 14,
                    marginBottom: 20,
                  }}
                >
                  <MetricCard
                    icon={Package}
                    label="Total Waste Items"
                    value={totalWasteItemCount}
                    loading={!data}
                    delay={0}
                  />
                  <MetricCard
                    icon={DollarSign}
                    label="Waste Value Identified"
                    value="Coming Soon"
                    delay={60}
                  />
                  <MetricCard
                    icon={TrendingDown}
                    label="Avg Waste per Case"
                    value="Coming Soon"
                    delay={120}
                  />
                </div>
              </div>
            )
          ) : (
            <EmptyState
              icon={Clock}
              title="Dead Time Data Not Available"
              message="Dead time reduction data is not yet being collected for this facility."
            />
          )}
        </section>

        {/* CORE: DRILL-DOWN */}
        <section style={{ padding: '0 0 28px' }}>
          <SectionHeader
            icon={Eye}
            title="Core"
            subtitle="Dive into individual procedures, surgeons, and ORs"
            actions={
              <PillToggle
                options={[
                  { label: 'By Surgeon', value: 'surgeon' },
                  { label: 'By Specialty', value: 'specialty' },
                  { label: 'By Procedure', value: 'procedure' },
                  { label: 'By OR', value: 'or' },
                ]}
                value={coreView}
                onChange={setCoreView}
              />
            }
          />

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                padding: '8px 14px',
                flex: 1,
                minWidth: 220,
              }}
            >
              <Search size={14} color={C.textFaint} />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search procedures, surgeons, specialties..."
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  color: '#fff',
                  fontSize: 13,
                  width: '100%',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <X size={14} color={C.textFaint} />
                </button>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                padding: '8px 14px',
              }}
            >
              <Filter size={14} color={C.textFaint} />
              <span style={{ fontSize: 12, color: C.textDim }}>Min duration:</span>
              <select
                value={minDuration}
                onChange={(e) => setMinDuration(Number(e.target.value))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.accent,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  outline: 'none',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {[
                  { v: 0, l: 'All' },
                  { v: 10, l: '10+ min' },
                  { v: 30, l: '30+ min' },
                  { v: 60, l: '60+ min' },
                ].map((o) => (
                  <option key={o.v} value={o.v} style={{ background: '#14161A' }}>
                    {o.l}
                  </option>
                ))}
              </select>
            </div>
            <ActionBtn icon={Download} label="Export All (CSV)" />
          </div>

          {coreView === 'surgeon' &&
            (loading ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 14,
                  marginBottom: 20,
                }}
              >
                {Array.from({ length: 4 }, (_, i) => (
                  <div
                    key={i}
                    style={{
                      background: C.card,
                      border: `1px solid ${C.cardBorder}`,
                      borderRadius: 14,
                      padding: '18px 20px',
                    }}
                  >
                    <div
                      className="loading-blur"
                      style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}
                    >
                      Surgeon Name
                    </div>
                    <div
                      className="loading-blur"
                      style={{ fontSize: 12, color: C.textDim, marginTop: 3 }}
                    >
                      Specialty
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
                      <div>
                        <div
                          className="loading-blur"
                          style={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: C.accent,
                            fontFamily: "'Space Mono', monospace",
                          }}
                        >
                          00
                        </div>
                        <div style={{ fontSize: 10, color: C.textFaint }}>Procedures</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : surgeons.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No Surgeons Found"
                message={
                  searchQuery
                    ? 'No surgeons match your search.'
                    : 'No surgeons have been onboarded at this facility yet.'
                }
              />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 14,
                  marginBottom: 20,
                }}
              >
                {surgeons.map((s, i) => (
                  <div
                    key={s.id}
                    style={{
                      background: C.card,
                      border: `1px solid ${C.cardBorder}`,
                      borderRadius: 14,
                      padding: '18px 20px',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s, background 0.2s',
                      animation: `insights-fadeSlideUp 0.4s ease ${i * 60}ms both`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = C.accentBorder;
                      e.currentTarget.style.background = 'rgba(0,207,160,0.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = C.cardBorder;
                      e.currentTarget.style.background = C.card;
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{s.name}</div>
                        <div style={{ fontSize: 12, color: C.textDim, marginTop: 3 }}>
                          {s.specialty} &middot; {s.profiles} profiles
                        </div>
                      </div>
                      {s.adoptionRate != null && (
                        <div
                          style={{
                            padding: '3px 8px',
                            borderRadius: 6,
                            background:
                              s.adoptionRate >= 80
                                ? 'rgba(0,207,160,0.1)'
                                : s.adoptionRate >= 60
                                  ? C.orangeDim
                                  : C.redDim,
                            fontSize: 11,
                            fontWeight: 600,
                            color:
                              s.adoptionRate >= 80
                                ? C.accent
                                : s.adoptionRate >= 60
                                  ? C.orange
                                  : C.red,
                          }}
                        >
                          {s.adoptionRate}%
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
                      <div>
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: C.accent,
                            fontFamily: "'Space Mono', monospace",
                          }}
                        >
                          {s.procedures}
                        </div>
                        <div style={{ fontSize: 10, color: C.textFaint }}>Procedures</div>
                      </div>
                      {s.wastePerCase != null && (
                        <div>
                          <div
                            style={{
                              fontSize: 20,
                              fontWeight: 700,
                              color: C.orange,
                              fontFamily: "'Space Mono', monospace",
                            }}
                          >
                            ${s.wastePerCase}
                          </div>
                          <div style={{ fontSize: 10, color: C.textFaint }}>Waste/Case</div>
                        </div>
                      )}
                      {s.hasBaseline && s.deadTimePerCase != null && (
                        <div>
                          <div
                            style={{
                              fontSize: 20,
                              fontWeight: 700,
                              color: C.purple,
                              fontFamily: "'Space Mono', monospace",
                            }}
                          >
                            -{s.deadTimePerCase}m
                          </div>
                          <div style={{ fontSize: 10, color: C.textFaint }}>DT/Case</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}

          {(coreView === 'specialty' || coreView === 'or') &&
            ((coreView === 'specialty' ? specialties : orRooms).length === 0 ? (
              <EmptyState
                icon={coreView === 'specialty' ? Layers : Monitor}
                title={coreView === 'specialty' ? 'No Specialties Found' : 'No OR Rooms Found'}
                message={
                  coreView === 'specialty'
                    ? 'No specialties have been recorded at this facility yet.'
                    : 'No operating rooms have been configured at this facility yet.'
                }
              />
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
                {(coreView === 'specialty' ? specialties : orRooms).map((item, i) => (
                  <div
                    key={item}
                    style={{
                      background: C.card,
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 12,
                      padding: '14px 20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      animation: `insights-fadeSlideUp 0.3s ease ${i * 40}ms both`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = C.accentBorder;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{item}</div>
                    <div style={{ fontSize: 12, color: C.textDim, marginTop: 4 }}>
                      {coreView === 'specialty'
                        ? `${surgeons.filter((s) => s.specialty === item).length} surgeon(s)`
                        : `${procedures.filter((p) => p.or === item).length} procedures`}
                    </div>
                  </div>
                ))}
              </div>
            ))}

          {/* Procedure table — always visible */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.cardBorder}`,
              borderRadius: 16,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '14px 16px',
                borderBottom: `1px solid ${C.cardBorder}`,
                display: 'grid',
                gridTemplateColumns: '1fr 140px 180px 60px 80px 70px 40px',
                fontSize: 10,
                color: C.textFaint,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}
            >
              <span>Procedure</span>
              <span>Date</span>
              <span>OR</span>
              <span>Duration</span>
              <span>Waste</span>
              <span>Dead Time</span>
              <span></span>
            </div>
            {loading ? (
              <>
                {Array.from({ length: 5 }, (_, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 140px 180px 60px 80px 70px 40px',
                      alignItems: 'center',
                      padding: '14px 16px',
                      borderBottom: `1px solid ${C.cardBorder}`,
                    }}
                  >
                    <div>
                      <div
                        className="loading-blur"
                        style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}
                      >
                        Procedure Name
                      </div>
                      <div
                        className="loading-blur"
                        style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}
                      >
                        Surgeon
                      </div>
                    </div>
                    <span className="loading-blur" style={{ fontSize: 12, color: C.textMid }}>
                      Jan 1, 2025
                    </span>
                    <span className="loading-blur" style={{ fontSize: 12, color: C.textMid }}>
                      OR 1
                    </span>
                    <span className="loading-blur" style={{ fontSize: 12, color: C.textMid }}>
                      00m
                    </span>
                    <span className="loading-blur" style={{ fontSize: 12, color: C.orange }}>
                      $0
                    </span>
                    <span className="loading-blur" style={{ fontSize: 12, color: C.accent }}>
                      0/0
                    </span>
                    <span />
                  </div>
                ))}
              </>
            ) : filteredProcedures.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: C.textFaint, fontSize: 13 }}>
                {procedures.length === 0
                  ? 'No procedure records found for this facility'
                  : 'No procedures match your filters'}
              </div>
            ) : (
              <>
                {pagedProcedures.map((proc, i) => (
                  <ProcedureRow
                    key={proc.id}
                    proc={proc}
                    index={i}
                    onClick={() => setSelectedProc(proc)}
                  />
                ))}
                {Array.from({ length: PROCEDURES_PER_PAGE - pagedProcedures.length }).map(
                  (_, i) => (
                    <div
                      key={`placeholder-${i}`}
                      style={{
                        height: 57,
                        borderBottom: `1px solid ${C.cardBorder}`,
                      }}
                    />
                  )
                )}
              </>
            )}
            {totalProcedurePages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '12px 16px',
                  borderTop: `1px solid ${C.cardBorder}`,
                }}
              >
                <button
                  onClick={() => setProcedurePage((p) => Math.max(0, p - 1))}
                  disabled={procedurePage === 0}
                  style={{
                    background: 'none',
                    border: `1px solid ${C.cardBorder}`,
                    borderRadius: 6,
                    color: procedurePage === 0 ? C.textFaint : C.textDim,
                    cursor: procedurePage === 0 ? 'default' : 'pointer',
                    fontSize: 12,
                    padding: '4px 10px',
                  }}
                >
                  Prev
                </button>
                <input
                  type="number"
                  min={1}
                  max={totalProcedurePages}
                  value={procedurePage + 1}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) {
                      setProcedurePage(Math.max(0, Math.min(totalProcedurePages - 1, val - 1)));
                    }
                  }}
                  style={{
                    width: 44,
                    textAlign: 'center',
                    background: 'none',
                    border: `1px solid ${C.cardBorder}`,
                    borderRadius: 6,
                    color: C.textDim,
                    fontSize: 12,
                    padding: '4px 6px',
                  }}
                />
                <span style={{ fontSize: 12, color: C.textFaint }}>/ {totalProcedurePages}</span>
                <button
                  onClick={() => setProcedurePage((p) => Math.min(totalProcedurePages - 1, p + 1))}
                  disabled={procedurePage === totalProcedurePages - 1}
                  style={{
                    background: 'none',
                    border: `1px solid ${C.cardBorder}`,
                    borderRadius: 6,
                    color: procedurePage === totalProcedurePages - 1 ? C.textFaint : C.textDim,
                    cursor: procedurePage === totalProcedurePages - 1 ? 'default' : 'pointer',
                    fontSize: 12,
                    padding: '4px 10px',
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ANOMALY FLAGS */}
        <section style={{ padding: '0 0 60px' }}>
          <SectionHeader
            icon={AlertTriangle}
            title="Anomaly Flags"
            subtitle="Procedures and patterns outside normal parameters"
            color={C.orange}
          />
          <EmptyState
            icon={AlertTriangle}
            title="No Anomalies Detected"
            message="Anomaly detection will be available once procedure-level metrics (waste, dead time, steps) are being collected."
          />
        </section>
      </div>

      {selectedProc && (
        <ProcedureDetail proc={selectedProc} onClose={() => setSelectedProc(null)} />
      )}
    </div>
  );
}
